/**
 * Nexus Abilities Page
 * Manage tool/ability toggles and settings
 */

import { useEffect, useState } from 'react'
import { nexusAbilitiesApi, Ability } from '@/services/nexusApi'

const categoryIcons: Record<string, string> = {
  payments: '💳',
  communication: '📱',
  contacts: '📇',
  research: '🔍',
  entertainment: '🎬',
  finance: '📊',
  productivity: '📋',
  creative: '🎨',
  transport: '🚗',
}

const statusColors: Record<string, string> = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  disabled: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  issues: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  maintenance: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
}

export default function NexusAbilities() {
  const [abilities, setAbilities] = useState<Ability[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<string>('all')
  const [updating, setUpdating] = useState<string | null>(null)

  useEffect(() => {
    loadAbilities()
  }, [])

  const loadAbilities = async () => {
    try {
      const data = await nexusAbilitiesApi.list()
      setAbilities(data.abilities)
    } catch (error) {
      console.error('Failed to load abilities:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleAbility = async (ability: Ability) => {
    setUpdating(ability.tool_name)
    try {
      await nexusAbilitiesApi.update(ability.tool_name, {
        is_enabled: !ability.is_enabled,
      })
      setAbilities((prev) =>
        prev.map((a) =>
          a.tool_name === ability.tool_name ? { ...a, is_enabled: !a.is_enabled } : a
        )
      )
    } catch (error) {
      console.error('Failed to toggle ability:', error)
    } finally {
      setUpdating(null)
    }
  }

  const setStatus = async (ability: Ability, status: string) => {
    setUpdating(ability.tool_name)
    try {
      await nexusAbilitiesApi.update(ability.tool_name, { status })
      setAbilities((prev) =>
        prev.map((a) =>
          a.tool_name === ability.tool_name ? { ...a, status } : a
        )
      )
    } catch (error) {
      console.error('Failed to update status:', error)
    } finally {
      setUpdating(null)
    }
  }

  // Group abilities by category
  const categories = [...new Set(abilities.map((a) => a.category))].sort()
  
  const filteredAbilities = filter === 'all' 
    ? abilities 
    : abilities.filter((a) => a.category === filter)

  const groupedAbilities = filteredAbilities.reduce((acc, ability) => {
    if (!acc[ability.category]) acc[ability.category] = []
    acc[ability.category].push(ability)
    return acc
  }, {} as Record<string, Ability[]>)

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-700 rounded w-48" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-slate-800 rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Abilities</h1>
          <p className="text-gray-400 mt-1">
            Manage tool access and settings
          </p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {categoryIcons[cat] || '📦'} {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <p className="text-sm text-gray-400">Total</p>
          <p className="text-2xl font-bold text-white">{abilities.length}</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <p className="text-sm text-gray-400">Enabled</p>
          <p className="text-2xl font-bold text-green-400">
            {abilities.filter((a) => a.is_enabled).length}
          </p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <p className="text-sm text-gray-400">Disabled</p>
          <p className="text-2xl font-bold text-gray-400">
            {abilities.filter((a) => !a.is_enabled).length}
          </p>
        </div>
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <p className="text-sm text-gray-400">Billable</p>
          <p className="text-2xl font-bold text-orange-400">
            {abilities.filter((a) => a.is_billable).length}
          </p>
        </div>
      </div>

      {/* Abilities by Category */}
      <div className="space-y-8">
        {Object.entries(groupedAbilities).map(([category, items]) => (
          <div key={category}>
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span>{categoryIcons[category] || '📦'}</span>
              {category.charAt(0).toUpperCase() + category.slice(1)}
              <span className="text-sm text-gray-500 font-normal">({items.length})</span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {items.map((ability) => (
                <div
                  key={ability.tool_name}
                  className={`bg-slate-800 rounded-xl p-5 border transition-all ${
                    ability.is_enabled ? 'border-slate-700' : 'border-slate-700/50 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-white font-medium">{ability.display_name}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded border ${statusColors[ability.status]}`}>
                          {ability.status}
                        </span>
                        {ability.is_billable && (
                          <span className="px-2 py-0.5 text-xs rounded bg-orange-500/20 text-orange-400 border border-orange-500/30">
                            Billable
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400 mt-1">{ability.description}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        Tool: <code className="text-purple-400">{ability.tool_name}</code>
                        {ability.is_billable && (
                          <> • Rate: KES {ability.billing_rate}</>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {/* Status Dropdown */}
                      <select
                        value={ability.status}
                        onChange={(e) => setStatus(ability, e.target.value)}
                        disabled={updating === ability.tool_name}
                        className="px-2 py-1 text-sm bg-slate-700 border border-slate-600 rounded text-white focus:outline-none"
                      >
                        <option value="active">Active</option>
                        <option value="issues">Issues</option>
                        <option value="maintenance">Maintenance</option>
                      </select>
                      {/* Toggle */}
                      <button
                        onClick={() => toggleAbility(ability)}
                        disabled={updating === ability.tool_name}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          ability.is_enabled ? 'bg-purple-600' : 'bg-slate-600'
                        }`}
                      >
                        <span
                          className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                            ability.is_enabled ? 'left-7' : 'left-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
